import { type ReactNode } from 'react';

import { Divider } from '../../../../../layouts/divider/Divider.js';
import { ChartTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { ChartDatapoint } from '../../../../core/types/chart-data.js';
import type { Formatter } from '../../../../core/types/formatter.js';
import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import type { XYChartTooltipPayload } from '../../../../xy-chart/types/xy-chart.js';
import { FLAME_ORIGINAL_IDX } from '../../constants.js';
import { formatFlameValue } from '../../utils/format-flame-value.js';

interface FlameTooltipProps<TData extends ChartDatapoint> {
  payload: XYChartTooltipPayload;
  data: readonly TData[];
  labelAccessor: string;
  valueField: string;
  subheader?: string;
  selfValueAccessor?: string;
  selfDescriptionLabel?: string;
  valueFormatter?: Formatter;
  seriesActions?: (datapoint: TData) => SeriesActionsTemplate;
}

export function FlameTooltip<TData extends ChartDatapoint>({
  payload,
  data,
  labelAccessor,
  valueField,
  subheader,
  selfValueAccessor,
  selfDescriptionLabel = 'Self',
  valueFormatter,
  seriesActions,
}: Readonly<FlameTooltipProps<TData>>): ReactNode {
  const datapoint = payload.rectPayload?.datapoint;

  if (!datapoint) {
    return null;
  }

  const rawLabel = datapoint?.[labelAccessor];
  const label =
    typeof rawLabel === 'string' ||
    typeof rawLabel === 'number' ||
    typeof rawLabel === 'boolean'
      ? String(rawLabel)
      : '';
  const selfDescription =
    selfValueAccessor !== undefined
      ? `${selfDescriptionLabel}: ${formatFlameValue(
          datapoint?.[selfValueAccessor],
          valueFormatter,
        )}`
      : undefined;
  const actions =
    seriesActions && datapoint
      ? () => seriesActions(data[datapoint[FLAME_ORIGINAL_IDX] as number])
      : undefined;

  return (
    <>
      <ChartTooltip.Header header1={label} subheader={subheader} />
      <Divider orientation="horizontal" style={{ marginBottom: '4px' }} />
      <ChartTooltip.SeriesItem
        color={`${payload.rectPayload?.color}`}
        name="Total"
        value={formatFlameValue(datapoint?.[valueField], valueFormatter)}
        description={selfDescription}
        actions={actions}
      />
    </>
  );
}
